import React, { useState } from 'react';
import { FileText, Download, Plus, Trash2, AlertTriangle, Calendar, RefreshCw } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Loading } from '../components/ui/Loading';
import { formatDate } from '../lib/utils';
import { useReports, useGenerateReport, useDownloadReport, useDeleteReport, useRetryReport } from '../hooks/useReports';
import { GenerateReportRequest, ReportType, ReportFormat } from '../types';

const Reports: React.FC = () => {
    const [page, setPage] = useState(1);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const { data, isLoading } = useReports(page, 20);
    const generateMutation = useGenerateReport();
    const downloadMutation = useDownloadReport();
    const deleteMutation = useDeleteReport();
    const retryMutation = useRetryReport();

    const [formData, setFormData] = useState<GenerateReportRequest>({
        type: 'daily',
        format: 'json',
    });

    const handleGenerate = async (e: React.FormEvent) => {
        e.preventDefault();

        // Build payload: only include dates for custom type
        const payload: GenerateReportRequest = {
            type: formData.type,
            format: formData.format,
        };

        // Only add dates if type is 'custom'
        if (formData.type === 'custom') {
            payload.period_start = formData.period_start;
            payload.period_end = formData.period_end;
        }

        // Debug: Log the payload being sent
        console.log('📤 Generating report with payload:', payload);

        try {
            await generateMutation.mutateAsync(payload);
            setShowGenerateModal(false);
            // Reset form
            setFormData({
                type: 'daily',
                format: 'json',
            });
        } catch (error) {
            // Error is already handled by mutation's onError
            // Keep modal open so user can change format
        }
    };

    const handleDownload = (reportId: string) => {
        downloadMutation.mutate(reportId);
    };

    const handleDelete = (reportId: string, reportType: string) => {
        if (window.confirm(`Are you sure you want to delete this ${reportType} report? This action cannot be undone.`)) {
            deleteMutation.mutate(reportId);
        }
    };

    const handleRetry = (reportId: string) => {
        retryMutation.mutate(reportId);
    };

    const handleChangePeriod = (report: any) => {
        // Open modal with report's settings and set to custom type with last 7 days
        setFormData({
            type: 'custom', // Change to custom to allow date modification
            format: report.format,
            period_start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            period_end: new Date().toISOString().split('T')[0],
        });
        setShowGenerateModal(true);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'text-green-700 bg-green-100';
            case 'processing':
            case 'pending':
                return 'text-yellow-700 bg-yellow-100';
            case 'failed':
                return 'text-red-700 bg-red-100';
            default:
                return 'text-gray-700 bg-gray-100';
        }
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return 'N/A';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(2)} MB`;
    };

    if (isLoading) {
        return <Loading fullScreen message="Loading reports..." />;
    }

    const reports = data?.data || [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
                    <p className="text-gray-600 mt-1">Generate and download payment reports</p>
                </div>
                <button
                    onClick={() => setShowGenerateModal(true)}
                    className="btn-primary flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    Generate Report
                </button>
            </div>

            {/* Reports List */}
            {reports.length === 0 ? (
                <Card>
                    <EmptyState
                        icon={FileText}
                        title="No reports found"
                        description="Generate your first report to get started"
                        action={{
                            label: 'Generate Report',
                            onClick: () => setShowGenerateModal(true),
                        }}
                    />
                </Card>
            ) : (
                <div className="space-y-4">
                    {reports.map((report) => (
                        <Card key={report.id}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-4 flex-1">
                                    <div className={`p-3 rounded-lg ${report.status === 'failed' ? 'bg-red-50' : 'bg-primary-50'
                                        }`}>
                                        {report.status === 'failed' ? (
                                            <AlertTriangle className="w-6 h-6 text-red-600" />
                                        ) : (
                                            <FileText className="w-6 h-6 text-primary-600" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-900 capitalize">
                                            {report.type} Report - {report.format.toUpperCase()}
                                        </h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Period: {formatDate(report.period_start)} - {formatDate(report.period_end)}
                                        </p>
                                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(report.status)} capitalize`}>
                                                {report.status === 'failed' ? '❌ Failed' : report.status}
                                            </span>
                                            {report.file_size && (
                                                <span className="text-xs text-gray-500">
                                                    {formatFileSize(report.file_size)}
                                                </span>
                                            )}
                                            {report.generated_at && (
                                                <span className="text-xs text-gray-500">
                                                    Generated: {formatDate(report.generated_at)}
                                                </span>
                                            )}
                                            {report.expires_at && (
                                                <span className="text-xs text-gray-500">
                                                    Expires: {formatDate(report.expires_at)}
                                                </span>
                                            )}
                                        </div>

                                        {/* Error Message for Failed Reports */}
                                        {report.status === 'failed' && report.error_message && (
                                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                <div className="flex items-start gap-2">
                                                    <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-red-800 mb-1">Report Generation Failed</p>
                                                        <p className="text-sm text-red-700">{report.error_message}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 mt-3">
                                                    <button
                                                        onClick={() => handleRetry(report.id)}
                                                        disabled={retryMutation.isPending}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                                                    >
                                                        <RefreshCw className={`w-3 h-3 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                                                        {retryMutation.isPending ? 'Retrying...' : 'Retry'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleChangePeriod(report)}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-red-300 text-red-700 text-xs font-medium rounded hover:bg-red-50 transition-colors"
                                                    >
                                                        <Calendar className="w-3 h-3" />
                                                        Change Period
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                    {report.status === 'completed' && (
                                        <button
                                            onClick={() => handleDownload(report.id)}
                                            disabled={downloadMutation.isPending}
                                            className="btn-primary flex items-center gap-2 disabled:opacity-50"
                                        >
                                            <Download className="w-4 h-4" />
                                            {downloadMutation.isPending ? 'Downloading...' : 'Download'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDelete(report.id, report.type)}
                                        disabled={deleteMutation.isPending}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {data && data.meta && data.meta.last_page > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-600">
                        Page {page} of {data.meta.last_page}
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(data.meta.last_page, p + 1))}
                        disabled={page === data.meta.last_page}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Generate Report Modal */}
            {showGenerateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <Card className="max-w-md w-full">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Generate Report</h2>
                            <button
                                onClick={() => setShowGenerateModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleGenerate} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Report Type
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value as ReportType })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    required
                                >
                                    <option value="daily">Daily (Today)</option>
                                    <option value="weekly">Weekly (Last 7 days)</option>
                                    <option value="monthly">Monthly (Last 30 days)</option>
                                    <option value="custom">Custom Date Range</option>
                                    <option value="audit">Audit Report (Last 30 days)</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    {formData.type === 'custom'
                                        ? '📅 Custom: Select your own date range below'
                                        : '⏰ Dates are calculated automatically for this report type'}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Format
                                </label>
                                <select
                                    value={formData.format}
                                    onChange={(e) => setFormData({ ...formData, format: e.target.value as ReportFormat })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    required
                                >
                                    <option value="json">JSON</option>
                                    <option value="csv">CSV (Professional Format)</option>
                                    <option value="pdf">PDF (Professional Format)</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    💡 CSV and PDF formats include improved layouts with statistics, provider breakdowns, and risk summaries.
                                </p>
                            </div>

                            {/* Date fields - Only show for custom type */}
                            {formData.type === 'custom' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Period Start
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.period_start || ''}
                                            onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Period End
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.period_end || ''}
                                            onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            required
                                        />
                                    </div>
                                </>
                            )}

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowGenerateModal(false)}
                                    className="flex-1 btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={generateMutation.isPending}
                                    className="flex-1 btn-primary"
                                >
                                    {generateMutation.isPending ? 'Generating...' : 'Generate'}
                                </button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default Reports;
